import { create } from 'zustand';
import {
  createStaff,
  listStaff,
  updateStaff,
  type StaffCreatePayload,
  type StaffPatch,
} from '@/lib/staff';
import type { Staff } from '@/lib/types';

/**
 * Staff store (Day 4).
 *
 * Lightweight cache of the org's staff directory. Mutations route through this
 * store so components stay in sync. Patterned after stores/locations.ts.
 */

interface StaffState {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  items: Staff[];
  error: string | null;

  load: () => Promise<void>;
  add: (payload: StaffCreatePayload) => Promise<Staff>;
  update: (id: string, patch: StaffPatch) => Promise<Staff>;
  /** Replace a single staff row in-place after an out-of-band mutation. */
  replace: (s: Staff) => void;
  reset: () => void;
}

export const useStaff = create<StaffState>((set) => ({
  status: 'idle',
  items: [],
  error: null,

  async load() {
    set({ status: 'loading', error: null });
    try {
      const items = await listStaff();
      set({ status: 'loaded', items });
    } catch (err) {
      set({ status: 'error', error: (err as Error).message });
    }
  },

  async add(payload) {
    const created = await createStaff(payload);
    set((s) => ({ items: [...s.items, created] }));
    return created;
  },

  async update(id, patch) {
    const updated = await updateStaff(id, patch);
    set((s) => ({
      items: s.items.map((it) => (it.id === id ? updated : it)),
    }));
    return updated;
  },

  replace(staff) {
    set((s) => ({
      items: s.items.map((it) => (it.id === staff.id ? staff : it)),
    }));
  },

  reset() {
    set({ status: 'idle', items: [], error: null });
  },
}));

export const useStaffCount = () => useStaff((s) => s.items.length);

export const useActiveStaff = () =>
  useStaff((s) =>
    s.items.filter((it) => {
      if (!it.active_to) return true;
      const today = new Date().toISOString().slice(0, 10);
      return it.active_to > today;
    })
  );
